/* eslint-disable react/no-multi-comp */
import {Text} from '@rdcomponents/text/text';
import {Button, Collapse, Form, Switch, Tooltip} from 'antd';

import {Locale} from '../../../../../provider/locale/locale';
import {classNames} from '../../../../../util/css';

import {FilterFormName, reviewsFilterFormId} from './reviews-filter-const';
import {ReviewsFilterFieldNameEnum} from './reviews-filter-type';
import {ReviewsFilterRatingSection} from './sections/reviews-filter-rating-section';
import {ReviewsFilterSectionAdditional} from './sections/reviews-filter-section-additional';
import {ReviewsFilterSectionEmployee} from './sections/reviews-filter-section-employee';
import {ReviewsFilterSectionLabel} from './sections/reviews-filter-section-label';
import {ReviewsFilterSectionMain} from './sections/reviews-filter-section-main';
import {ReviewsFilterSectionTags} from './sections/reviews-filter-section-tags';
import {ReviewsFilterSectionEnum, useReviewsFilter} from './use-reviews-filter';
import {useReviewsFilterFooterDock} from './use-reviews-filter-footer-dock';
import * as styles from './reviews-filter.scss';

type FilterHookType = ReturnType<typeof useReviewsFilter>;

type PropsType = {
    filterHook?: FilterHookType;
    isDrawerContent?: boolean;
};

// eslint-disable-next-line complexity -- filter form sections and collapse items
function ReviewsFilterForm(props: {filterHook: FilterHookType; isDrawerContent: boolean}): JSX.Element {
    const {filterHook, isDrawerContent} = props;

    const {
        form,
        onFieldsChange,
        onFormReset,
        handleDrawerApply,
        expandedSections,
        reviewFilterState,
        reviewsFilterSectionActiveMap,
        isFilterEmpty,
        isSavedFilterEnabled,
        isSavedFilterLoading,
        isSavedFilterSaving,
        ratingOptions,
        totalReviewsLabelCount,
        displayedAverageRating,
        isReviewsStatisticFetching,
        sourcesOptions,
        reviewsCatalogListIsInProgress,
        hasDoctors,
        reviewsDoctorList,
        reviewsDoctorListIsInProgress,
        reviewStatusList,
        isReviewStatusListInProgress,
        responsibleUsersList,
        isReviewResponsibleUsersInProgress,
        reviewsReplierList,
        reviewsReplierListIsInProgress,
        internalAgencyReviewLicense,
        isShowAutotags,
        periodPickerValue,
        periodPickerActiveTab,
        watchedReplyDateFrom,
        watchedReplyDateTo,
        replyDateFrom,
        replyDateTo,
        getLocalizedString,
        handleSectionsChange,
        handleSavedFilterToggle,
        handleSavedFilterKeyDown,
        handleRatingChange,
        handleSourceListChange,
        handleDoctorsChange,
        handleStatusListChange,
        handleRepliesToReviewsChange,
        handleContentOfReviewsChange,
        handleHasNotesChange,
        handleHiddenReviewsChange,
        handlePossibilityOfAnswerChange,
        handleDataRangeChange,
        handleCollectionDateRangeChange,
        handlePeriodCalendarChange,
        handlePeriodPickerTabChange,
        handleResponsibleEmployeeChange,
        handleRespondingEmployeeChange,
        handleAgencyRepliersChange,
        handleEmployeeReplyDateRangeChange,
        handleWithoutTagChange,
        handleNewTagsChange,
        handleDeletedReviewsChange,
        handleAbuseStatusChange,
        handleTonalityChange,
        handleAutoReplyChange,
        handleAutoAbuseChange,
        handleReviewChangedChange,
        handleWaitingForSecondAnswerChange,
        handleReviewTypeChange,
        handleModeratedReviewChange,
        handleReplyPublishedChange,
    } = filterHook;

    const isReviewsFilterFooterVisible = !isFilterEmpty || isSavedFilterEnabled;
    const {fixedFooterBox, footerRef, isFilterFooterFixed, scrollContentRef, scrollRootRef} =
        useReviewsFilterFooterDock(isFilterEmpty, isReviewsFilterFooterVisible && !isDrawerContent);

    const isSaveDisabled = isSavedFilterLoading || isSavedFilterSaving;

    const collapseItems = [
        {
            key: ReviewsFilterSectionEnum.main,
            label: (
                <ReviewsFilterSectionLabel
                    isMarkVisible={
                        reviewsFilterSectionActiveMap[ReviewsFilterSectionEnum.main] &&
                        !expandedSections.includes(ReviewsFilterSectionEnum.main)
                    }
                    stringKey="REVIEWS__FILTER__SECTION__MAIN"
                />
            ),
            children: (
                <ReviewsFilterSectionMain
                    form={form}
                    getLocalizedString={getLocalizedString}
                    hasDoctors={hasDoctors}
                    isReviewStatusListInProgress={isReviewStatusListInProgress}
                    onCollectionDateRangeChange={handleCollectionDateRangeChange}
                    onContentOfReviewsChange={handleContentOfReviewsChange}
                    onDataRangeChange={handleDataRangeChange}
                    onDoctorsChange={handleDoctorsChange}
                    onPeriodCalendarChange={handlePeriodCalendarChange}
                    onPeriodPickerTabChange={handlePeriodPickerTabChange}
                    onPossibilityOfAnswerChange={handlePossibilityOfAnswerChange}
                    onRepliesToReviewsChange={handleRepliesToReviewsChange}
                    onSourceListChange={handleSourceListChange}
                    onStatusListChange={handleStatusListChange}
                    periodPickerActiveTab={periodPickerActiveTab}
                    periodPickerValue={periodPickerValue}
                    reviewFilterState={reviewFilterState}
                    reviewStatusList={reviewStatusList}
                    reviewsCatalogListIsInProgress={reviewsCatalogListIsInProgress}
                    reviewsDoctorList={reviewsDoctorList}
                    reviewsDoctorListIsInProgress={reviewsDoctorListIsInProgress}
                    sourcesOptions={sourcesOptions}
                />
            ),
        },
        {
            key: ReviewsFilterSectionEnum.employee,
            label: (
                <ReviewsFilterSectionLabel
                    isMarkVisible={
                        reviewsFilterSectionActiveMap[ReviewsFilterSectionEnum.employee] &&
                        !expandedSections.includes(ReviewsFilterSectionEnum.employee)
                    }
                    stringKey="REVIEWS__FILTER__SECTION__EMPLOYEE"
                />
            ),
            children: (
                <ReviewsFilterSectionEmployee
                    getLocalizedString={getLocalizedString}
                    internalAgencyReviewLicense={internalAgencyReviewLicense}
                    isReviewResponsibleUsersInProgress={isReviewResponsibleUsersInProgress}
                    onAgencyRepliersChange={handleAgencyRepliersChange}
                    onEmployeeReplyDateRangeChange={handleEmployeeReplyDateRangeChange}
                    onRespondingEmployeeChange={handleRespondingEmployeeChange}
                    onResponsibleEmployeeChange={handleResponsibleEmployeeChange}
                    replyDateFrom={replyDateFrom}
                    replyDateTo={replyDateTo}
                    responsibleUsersList={responsibleUsersList}
                    reviewFilterState={reviewFilterState}
                    reviewsReplierList={reviewsReplierList}
                    reviewsReplierListIsInProgress={reviewsReplierListIsInProgress}
                    watchedReplyDateFrom={watchedReplyDateFrom}
                    watchedReplyDateTo={watchedReplyDateTo}
                />
            ),
        },
        {
            key: ReviewsFilterSectionEnum.tags,
            label: (
                <ReviewsFilterSectionLabel
                    isMarkVisible={
                        reviewsFilterSectionActiveMap[ReviewsFilterSectionEnum.tags] &&
                        !expandedSections.includes(ReviewsFilterSectionEnum.tags)
                    }
                    stringKey="REVIEWS__FILTER__SECTION__TAGS"
                />
            ),
            children: (
                <ReviewsFilterSectionTags
                    form={form}
                    isShowAutotags={Boolean(isShowAutotags)}
                    onNewTagsChange={handleNewTagsChange}
                    onWithoutTagChange={handleWithoutTagChange}
                    reviewFilterState={reviewFilterState}
                />
            ),
        },
        {
            key: ReviewsFilterSectionEnum.additional,
            label: (
                <ReviewsFilterSectionLabel
                    isMarkVisible={
                        reviewsFilterSectionActiveMap[ReviewsFilterSectionEnum.additional] &&
                        !expandedSections.includes(ReviewsFilterSectionEnum.additional)
                    }
                    stringKey="REVIEWS__FILTER__SECTION__ADDITIONAL"
                />
            ),
            children: (
                <ReviewsFilterSectionAdditional
                    form={form}
                    onAbuseStatusChange={handleAbuseStatusChange}
                    onAutoAbuseChange={handleAutoAbuseChange}
                    onAutoReplyChange={handleAutoReplyChange}
                    onDeletedReviewsChange={handleDeletedReviewsChange}
                    onHasNotesChange={handleHasNotesChange}
                    onHiddenReviewsChange={handleHiddenReviewsChange}
                    onModeratedReviewChange={handleModeratedReviewChange}
                    onReplyPublishedChange={handleReplyPublishedChange}
                    onReviewChangedChange={handleReviewChangedChange}
                    onReviewTypeChange={handleReviewTypeChange}
                    onTonalityChange={handleTonalityChange}
                    onWaitingForSecondAnswerChange={handleWaitingForSecondAnswerChange}
                    reviewFilterState={reviewFilterState}
                />
            ),
        },
    ];

    const formClassName = classNames(styles.ReviewsFilter, {
        [styles.ReviewsFilter_drawerContent]: isDrawerContent,
    });

    const scrollableClassName = classNames(styles.ReviewsFilter_scrollable, {
        [styles.ReviewsFilter_drawerScrollable]: isDrawerContent,
    });

    return (
        <Form
            className={formClassName}
            form={form}
            id={isDrawerContent ? reviewsFilterFormId : FilterFormName}
            layout="vertical"
            name={FilterFormName}
            onFinish={isDrawerContent ? handleDrawerApply : () => null}
            onReset={onFormReset}
            onValuesChange={onFieldsChange}
        >
            <div className={scrollableClassName} ref={scrollRootRef}>
                <div ref={scrollContentRef}>
                    {!isDrawerContent && (
                        <div className={styles.ReviewsFilter_header}>
                            <Text bolder large>
                                <Locale stringKey="BUTTON__FILTER" />
                            </Text>
                        </div>
                    )}

                    <ReviewsFilterRatingSection
                        displayedAverageRating={displayedAverageRating}
                        isReviewsStatisticFetching={isReviewsStatisticFetching}
                        onRatingChange={handleRatingChange}
                        ratingList={reviewFilterState[ReviewsFilterFieldNameEnum.ratingList]}
                        ratingOptions={ratingOptions}
                        totalReviewsLabelCount={totalReviewsLabelCount}
                    />

                    <Collapse
                        activeKey={expandedSections}
                        bordered={false}
                        className={styles.ReviewsFilter_collapse}
                        expandIconPosition="end"
                        ghost
                        items={collapseItems}
                        onChange={handleSectionsChange}
                    />
                </div>
            </div>

            {!isDrawerContent && isReviewsFilterFooterVisible && (
                <>
                    {isFilterFooterFixed && fixedFooterBox.height > 0 && (
                        <div
                            aria-hidden
                            className={styles.ReviewsFilter_footerSpacer}
                            style={{height: fixedFooterBox.height}}
                        />
                    )}

                    <div
                        className={classNames(
                            styles.ReviewsFilter_footer,
                            isFilterFooterFixed ? styles.ReviewsFilter_footer_fixed : ''
                        )}
                        ref={footerRef}
                        style={isFilterFooterFixed ? {left: fixedFooterBox.left, width: fixedFooterBox.width} : {}}
                    >
                        {!isFilterEmpty && (
                            <Button className={styles.ReviewsFilter_footerButton} htmlType="reset" type="link">
                                <Locale stringKey="REVIEWS__FILTER__RESET" />
                            </Button>
                        )}
                        <Tooltip
                            title={
                                isSavedFilterEnabled ? null : (
                                    <Locale stringKey="REVIEWS__FILTER__SAVE_FILTERS_TOOLTIP" />
                                )
                            }
                        >
                            <div
                                aria-checked={isSavedFilterEnabled}
                                aria-disabled={isSaveDisabled}
                                className={classNames(
                                    styles.ReviewsFilter_saveSwitch,
                                    isSaveDisabled ? styles.ReviewsFilter_saveSwitch_disabled : ''
                                )}
                                onClick={handleSavedFilterToggle}
                                onKeyDown={handleSavedFilterKeyDown}
                                role="switch"
                                tabIndex={isSaveDisabled ? -1 : 0}
                            >
                                <Switch
                                    checked={isSavedFilterEnabled}
                                    disabled={isSaveDisabled}
                                    loading={isSavedFilterSaving}
                                    size="small"
                                />
                                <Text className={styles.ReviewsFilter_saveSwitchText}>
                                    <Locale stringKey="REVIEWS__FILTER__SAVE_FILTERS" />
                                </Text>
                            </div>
                        </Tooltip>
                    </div>
                </>
            )}
        </Form>
    );
}

function ReviewsFilterWithHook(): JSX.Element {
    const filterHook = useReviewsFilter();

    return <ReviewsFilterForm filterHook={filterHook} isDrawerContent={false} />;
}

export function ReviewsFilter(props: PropsType): JSX.Element {
    const {filterHook: externalFilterHook, isDrawerContent = false} = props;

    if (externalFilterHook) {
        return <ReviewsFilterForm filterHook={externalFilterHook} isDrawerContent={isDrawerContent} />;
    }

    return <ReviewsFilterWithHook />;
}
